import { createContext, useContext, useMemo, useRef, useCallback } from 'react';
import { AppContext } from './AppContext.tsx';
import type { Tab } from '../types/tab.ts';

interface TabContextType {
  tab: Tab | null;
  updateTab: (updates: Partial<Tab>) => void;
}

const TabContext = createContext<TabContextType>({
  tab: null,
  updateTab: () => {},
});

export const TabProvider = ({ children }) => {
  const { tabs, updateTab, activeTabId } = useContext(AppContext);

  // Derive active tab directly from tabs + activeTabId (avoids getActiveTab dep cycle)
  const activeTab = useMemo(() => {
    if (!activeTabId) return null;
    return tabs.find(t => t.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  // Keep a stable ref for the tab id to avoid recreating handleUpdateTab
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;

  const handleUpdateTab = useCallback(
    (updates: Partial<Tab>) => {
      const id = activeTabIdRef.current;
      if (id) updateTab(id, updates);
    },
    [updateTab]
  );

  const value = useMemo(
    () => ({
      tab: activeTab,
      updateTab: handleUpdateTab,
    }),
    [activeTab, handleUpdateTab]
  );

  return (
    <TabContext.Provider value={value}>
      {children}
    </TabContext.Provider>
  );
};

export const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within TabProvider');
  }
  return context;
};

export default TabContext;
